import useOffers from 'hooks/Offers/useOffers'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import getFlightDetailsByOrderItems from 'lib/flights/getFlightDetailsByOrderItems'
import { isCoverGeniusEnabled, isCoverGeniusEndDateValid, isInsuranceStartDateValid } from 'lib/insurance/insuranceHelpers'
import { getInsuranceDatesFromOrder, getInsuranceDestinationCountriesFromOrder } from 'lib/order/orderInsuranceUtils'
import { isGroupPaymentParticipantOrder } from 'lib/payment/groupPaymentDetailsUtils'
import { getCanViewLuxPlusBenefits, getIsEnabledLuxPlusDiscountedInsurance } from 'luxPlus/selectors/featureToggle'
import { useMemo } from 'react'
import isInsuranceBookingProtectionPostPurchaseEnabled from 'selectors/isInsuranceBookingProtectionPostPurchaseEnabled'

function useInsuranceUpsellCommonData(order: App.Order) {
  // Luxplus
  const fetchingLuxPlusSubscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription.fetching)
  const ableToViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusDiscountedInsuranceEnabled = useAppSelector(getIsEnabledLuxPlusDiscountedInsurance)

  // Offers
  const offerIds = useMemo(() => [
    ...order.items.map((i) => i.offer.id),
    ...order.cruiseItems.map((i) => i.cruiseOfferId),
  ], [order.cruiseItems, order.items])
  const [offers, fetching] = useOffers(offerIds)

  // Insurance Dates
  const dates = useMemo(() => getInsuranceDatesFromOrder(order), [order])
  const datesAreValid = dates?.endDate && dates.startDate ?
    isInsuranceStartDateValid(dates.startDate) && isCoverGeniusEndDateValid(dates.endDate) : false

  // Insurance destination countries
  const { cruiseOffers, allOffers, flightDetails, experienceOffers } = useAppSelector(
    state => ({
      cruiseOffers: state.cruise.cruiseOfferSnapshot,
      allOffers: state.offer.offers,
      flightDetails: state.orders.flightDetails,
      experienceOffers: state.experience.experiences,
    }),
    shallowEqual,
  )
  const flightDetailsByOrderItems = useMemo(() => getFlightDetailsByOrderItems(order, flightDetails), [order, flightDetails])
  const destCountriesFromOrder = useMemo(() =>
    getInsuranceDestinationCountriesFromOrder(order, cruiseOffers, allOffers, flightDetailsByOrderItems, experienceOffers),
  [cruiseOffers, order, allOffers, flightDetailsByOrderItems, experienceOffers])

  // Insurance Booking Protection flags
  const isInsuranceBookingProtectionSupported = useAppSelector(state => isInsuranceBookingProtectionPostPurchaseEnabled(state, order))

  // Insurance flags
  const coverGeniusIsEnabled = useAppSelector(state => isCoverGeniusEnabled(state.geo.currentRegionCode))
  const isInsuranceSupported = !!(dates && destCountriesFromOrder.length) && coverGeniusIsEnabled && !isInsuranceBookingProtectionSupported && !isGroupPaymentParticipantOrder(order)
  const orderHasInsurance = order.hasInsurance

  return {
    luxplus: {
      fetchingLuxPlusSubscription,
      ableToViewLuxPlusBenefits,
      luxPlusDiscountedInsuranceEnabled,
    },
    offers: {
      offers,
      fetching,
    },
    datesAreValid,
    flags: {
      isInsuranceSupported,
      orderHasInsurance,
      isInsuranceBookingProtectionSupported,
    },
  }
}

export default useInsuranceUpsellCommonData
